// src/utils/timeFormatUtils.js
import dayjs from "@/plugins/dayjs";

/**
 * Normalizes a date range to UTC milliseconds.
 * Handles both number (UTC ms) and Date object inputs.
 *
 * @param {Date|number} startDate - Start date (Date object or UTC milliseconds)
 * @param {Date|number} endDate - End date (Date object or UTC milliseconds)
 * @returns {{start: number, end: number}|null} Normalized date range or null if invalid
 */
export function normalizeDateRange(startDate, endDate) {
    if (typeof startDate === "number" && typeof endDate === "number") {
        return { start: startDate, end: endDate };
    }
    if (startDate && endDate) {
        return {
            start: dayjs(startDate).startOf("day").utc().valueOf(),
            end: dayjs(endDate).endOf("day").utc().valueOf(),
        };
    }
    return null;
}

/**
 * Formats time from decimal hours to "Xh:Ymin" string.
 *
 * @param {number} hours - Time in decimal hours
 * @returns {string} Formatted time string or "N/A" if zero/invalid
 */
export function formatTimeFromHours(hours) {
    if (!hours || hours === 0) return "N/A";
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h:${m}min`;
}

/**
 * Formats time from milliseconds to "Xh:Ym" string.
 *
 * @param {number} milliseconds - Time in milliseconds
 * @returns {string} Formatted time string or "N/A" if zero/invalid
 */
export function formatTimeFromMs(milliseconds) {
    if (!milliseconds || milliseconds === 0) return "N/A";
    const totalMinutes = Math.round(milliseconds / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}h:${minutes}m`;
}
